import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { endpoints } from './api';
import { Field } from './components';

type TypeOption = { id: number; name: string; groupName?: string; categoryName?: string };
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** Select a real type by name without rendering the entire SDE as native options. */
export function TypeSearch({ label, name, options }: { label: string; name: string; options: TypeOption[] }) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TypeOption | null>(null);
  const [remote, setRemote] = useState<TypeOption[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [manual, setManual] = useState(false);
  const matches = useMemo(() => {
    const term = normalize(query);
    return [...new Map([...options, ...remote].map((item) => [item.id, item])).values()]
      .filter((item) => !term || term.split(/\s+/).every((token) => normalize(item.name).includes(token)) || String(item.id) === term)
      .sort((a, b) => Number(normalize(b.name).startsWith(term)) - Number(normalize(a.name).startsWith(term)) || a.name.localeCompare(b.name))
      .slice(0, 20);
  }, [options, remote, query]);
  useEffect(() => {
    if (manual || !open || query.trim().length < 2 || selected?.name === query) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setFailed(false); setRemote([]);
    const timer = window.setTimeout(() => {
      void endpoints.searchTypes(query.trim()).then((items) => { if (!cancelled) setRemote(items); })
        .catch(() => { if (!cancelled) setFailed(true); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, manual, open, selected?.name]);
  useEffect(() => {
    const form = root.current?.closest('form');
    function reset() { setQuery(''); setSelected(null); setRemote([]); setOpen(false); setManual(false); }
    form?.addEventListener('reset', reset);
    return () => form?.removeEventListener('reset', reset);
  }, []);
  function choose(item: TypeOption) { setSelected(item); setQuery(item.name); setOpen(false); setActive(0); }
  return <div className="type-search" ref={root}>
    <Field label={label} hint={manual ? 'Identificador del tipo en EVE' : selected ? `Seleccionado: ${selected.name} · ID ${selected.id}` : undefined}>
      {manual ? <input required name={name} type="number" min="1" placeholder="Type ID de EVE" /> : <>
        <input type="hidden" name={name} value={selected?.id ?? ''} />
        <input required autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-results`} aria-activedescendant={open && matches[active] ? `${id}-${active}` : undefined}
          value={query} placeholder="Busca por nombre" onFocus={() => setOpen(true)}
          onChange={(event) => { setQuery(event.target.value); setSelected(null); setActive(0); setOpen(true); event.currentTarget.setCustomValidity('Selecciona un resultado de la búsqueda.'); }}
          onBlur={(event) => { event.currentTarget.setCustomValidity(selected ? '' : 'Selecciona un resultado de la búsqueda.'); if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node)) setOpen(false); }}
          ref={(element) => { element?.setCustomValidity(selected ? '' : 'Selecciona un resultado de la búsqueda.'); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); setActive((index) => Math.max(0, Math.min(matches.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))); }
            if (event.key === 'Enter' && open && matches[active]) { event.preventDefault(); choose(matches[active]); }
          }} />
        {open && <div className="type-search-results" role="listbox" id={`${id}-results`} aria-label={`Resultados para ${label}`}>
          {matches.map((item, index) => <button type="button" role="option" aria-selected={index === active} id={`${id}-${index}`} key={item.id} onPointerDown={(event) => event.preventDefault()} onClick={() => choose(item)}><strong>{item.name}</strong><small>{item.groupName ?? item.categoryName ?? `Type ID ${item.id}`}</small></button>)}
          {loading && <p role="status">Buscando en el catálogo…</p>}
          {failed && <p role="status">El catálogo no responde. Se muestran las coincidencias de tus datos.</p>}
          {!loading && matches.length === 0 && <p role="status">{query.trim().length < 2 ? 'Escribe al menos dos letras.' : 'Sin coincidencias. Prueba otra parte del nombre.'}</p>}
          {matches.length === 20 && <p>Primeros 20 resultados. Escribe más para afinar.</p>}
        </div>}
      </>}
    </Field>
    <button type="button" className="link" onClick={() => { setManual(!manual); setSelected(null); setQuery(''); setOpen(false); }}>{manual ? 'Buscar por nombre' : 'Usar Type ID'}</button>
  </div>;
}
